import { inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BaseViewModel } from "../../../../shared/base-view-model";
import { ChartData } from "../../../../shared/charts/models/chart-data.model";
import { DoughnutChartSettings } from "../../../../shared/charts/models/chart-settings.model";
import { doughnutChartSettings } from "../../../../shared/components/campaign-dashboard-stats-section/chart-settings/doughnut-chart-settings";
import { engagementChartData } from "../../../../shared/components/campaign-dashboard-stats-section/chart-settings/engagement-chart-data-settings";
import { partnerStatusChartData } from "../../../../shared/components/campaign-dashboard-stats-section/chart-settings/partner-status-chart-data-settings";
import { ChartLegendLineWithColor } from "../../../../shared/components/doughnut-chart-with-legend/doughnut-chart-with-legend.component";
import { BrandCampaignInsight } from "../../../../shared/models/brandCampaignInsight";
import { BrandCampaignInsightService } from "../../shared/services/brand-campaign-insight.service";
import { BrandCampaignPartnerStatusChartDialogViewState } from "./brand-campaign-partner-status-chart-dialog.view-state";

export class BrandCampaignPartnerStatusChartDialogViewModel extends BaseViewModel<BrandCampaignPartnerStatusChartDialogViewState> {
  private readonly contentEngagementInfoURL = inject(TranslateService).instant(
    "url.contentEngagement",
  );
  constructor(
    private readonly brandCampaignInsightService: BrandCampaignInsightService,
    private readonly campaignId: number,
    state?: BrandCampaignPartnerStatusChartDialogViewState,
  ) {
    super(state ?? { $type: "loading" });
  }

  public async init(): Promise<void> {
    this.setState({ $type: "loading" });

    try {
      const insights =
        await this.brandCampaignInsightService.getBrandCampaignInsights(
          this.campaignId,
        );

      this.setState({
        $type: "loaded",
        engagementPieChartSettings: this.getEngagementChartSettings(insights),
        partnerStatusPieChartSettings:
          this.getPartnerStatusChartSettings(insights),
        totalEngagedPartners: insights.totalEngagedPartners,
        totalPartners: insights.totalPartners,
        engagementLegends: this.getEngagementLegends(insights),
        partnerStatusLegends: this.getPartnerStatusLegends(insights),
        contentEngagementInfoURL: this.contentEngagementInfoURL,
      });
    } catch {
      this.setState({ $type: "error" });
    }
  }

  private getPartnerStatusLegends(insights: BrandCampaignInsight) {
    return partnerStatusChartData
      .map((item) => {
        if (!insights || !(item.prop in insights.partnerStatusChart)) {
          return undefined;
        }

        return {
          amount: insights.partnerStatusChart[item.prop],
          text: "brand.campaign.dashboard.engagementLevelsChart." + item.prop,
          color: item.color,
        };
      })
      .filter(
        (item): item is ChartLegendLineWithColor => typeof item !== "undefined",
      );
  }

  private getEngagementLegends(insights: BrandCampaignInsight) {
    return engagementChartData
      .map((item) => {
        if (!insights || !(item.prop in insights.engagementChart)) {
          return undefined;
        }

        return {
          amount: insights.engagementChart[item.prop],
          text: "brand.campaign.dashboard.engagementLevelsChart." + item.prop,
          color: item.color,
        };
      })
      .filter(
        (item): item is ChartLegendLineWithColor => typeof item !== "undefined",
      );
  }

  private getEngagementChartSettings(insights: BrandCampaignInsight) {
    return new DoughnutChartSettings(
      doughnutChartSettings,
      engagementChartData
        .map((item) =>
          item.prop in insights.engagementChart
            ? new ChartData(
                `brand.campaign.dashboard.engagementLevelsChart.${item.prop}`,
                insights.engagementChart[item.prop] ?? 0,
                String(item.color),
              )
            : undefined,
        )
        .filter((item): item is ChartData => typeof item !== "undefined"),
    );
  }

  private getPartnerStatusChartSettings(insights: BrandCampaignInsight) {
    return new DoughnutChartSettings(
      doughnutChartSettings,
      partnerStatusChartData.map(
        (item) =>
          new ChartData(
            `brand.campaign.dashboard.engagementLevelsChart.${item.prop}`,
            insights.partnerStatusChart[item.prop] ?? 0,
            String(item.color),
          ),
      ),
    );
  }
}
